import { Body, Box } from '@hover/blueprint';
import { iconExternalLink } from '@hover/icons';
import React from 'react';
import { Trans } from 'react-i18next';
import { useHistory } from 'react-router';
import type { AiDisplayImage } from 'src/../mock-server/src/generated/types';

import { useHomeownerAppContext } from 'src/modules/homeownerApp/hooks';
import { HomeownerAppMenuNames } from 'src/modules/homeownerApp/providers/HomeownerAppProvider';
import { Button } from 'src/shared/components';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { linkPressed } from 'src/shared/telemetry';

import { MaterialCard } from '../../Card';
import type { RegionWithDisabledRegions } from '../../FullscreenImage/useHumanReadableEdits';
import { mapRegionToDisplayText } from '../../FullscreenImage/useHumanReadableEdits';

export type Swatch = {
  uid: string;
  thumbnail: string;
  color?: string;
  image?: string;
  subtitle?: string;
  linkOutCta?: string;
  linkOutUrl?: string;
  materialName?: string;
  region?: RegionWithDisabledRegions;
  assets: AiDisplayImage[];
};

const ProjectSwatch = ({
  color,
  thumbnail,
  projectId,
  image,
  title,
  subtitle,
  linkOutCta,
  linkOutUrl,
  uid,
  region,
  materialName,
  assets,
}: Swatch & { projectId: string; title?: string }) => {
  const history = useHistory();
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const { trackingData, user, setCurrentMenu } = useHomeownerAppContext();

  const header = region ? (
    <Trans
      components={{
        Bold: <Body as="span" color="inherit" isSemiBold />,
      }}
      i18nKey="projects.swatch.cardHeader"
      t={t}
      values={{
        region: mapRegionToDisplayText(t, region),
        materialName,
      }}
    />
  ) : (
    materialName
  );

  return (
    <Box flexDirection="column" key={uid}>
      <Box mb={300} minHeight={600}>
        <MaterialCard
          backgroundColor={color}
          backgroundImage={image}
          count={assets.length}
          coverImageAlt={t('projects.swatch.pictureInPictureAlt', {
            edit: color,
          })}
          coverImageUrl={thumbnail}
          cursor="pointer"
          header={header}
          onClick={() => {
            history.replace({
              pathname: location.pathname,
              state: {
                header: title,
                subtitle: region
                  ? t('projects.swatch.cardHeaderTextOnly', {
                      region: mapRegionToDisplayText(t, region),
                      materialName,
                    })
                  : materialName,
                assets,
                projectId,
              },
            });
            setCurrentMenu(HomeownerAppMenuNames.IMAGE_COLLECTION);
          }}
          subtitle={subtitle}
        />
      </Box>
      {user?.isHomeowner && linkOutCta && (
        <>
          <Button
            as="a"
            href={linkOutUrl}
            iconAfter={iconExternalLink}
            includeTrackingImpression
            optionalTrackingProperties={{
              track_id: 'sample-link-out',
              destination_url: linkOutUrl,
              project_id: Number(projectId),
              link_location: 'Paint Tile',
              ...trackingData,
            }}
            target="_blank"
            textDecoration="none"
            track={linkPressed}
          >
            {linkOutCta}
          </Button>
        </>
      )}
    </Box>
  );
};

export { ProjectSwatch };
